import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CALLBACK_URL } from '../config/auth0';
import { startAuth0PKCE } from '../utils/auth0pkce';
import { refreshToken } from '../utils/tokenRefresh';
import { notifyAuthChange } from './useUser';

// Délai minimum entre deux redirections vers Auth0 (évite les boucles rapides)
const REDIRECT_COOLDOWN_MS = 10_000; // 10 secondes
let lastRedirectTime = 0;

/**
 * Hook pour gérer les erreurs d'authentification globales
 * ⚠️ Ne pas supprimer le token ni rediriger immédiatement sur chaque 401 :
 * useUser gère ses propres retries. Ce hook n'intervient qu'en dernier recours.
 */
export function useAuthErrorHandler() {
  const navigate = useNavigate();
  const handlingRef = useRef(false);

  useEffect(() => {
    const handleAuthError = async (event: WindowEventMap['auth:error']) => {
      const { status, message } = event.detail;
      
      console.warn('🚨 auth:error reçu:', { status, message });
      
      if (status !== 401) return;

      // Éviter les traitements simultanés
      if (handlingRef.current) {
        console.log('⏸️ auth:error ignoré — traitement déjà en cours');
        return;
      }

      // Éviter les redirections en boucle rapide
      const now = Date.now();
      if (now - lastRedirectTime < REDIRECT_COOLDOWN_MS) {
        console.log('⏸️ auth:error ignoré — cooldown actif');
        return;
      }

      // Ne pas rediriger si on est sur une page callback (login en cours)
      if (window.location.pathname.includes('/auth/') || window.location.pathname.includes('/callback')) {
        console.log('⏸️ auth:error ignoré — page callback en cours');
        return;
      }

      handlingRef.current = true;

      try {
        // 1️⃣ Essayer d'abord un refresh token avant tout
        const refreshTokenValue = localStorage.getItem('auth0_refresh_token');
        if (refreshTokenValue) {
          console.log('🔄 Tentative de refresh token avant redirection...');
          const newToken = await refreshToken();
          if (newToken) {
            console.log('✅ Refresh réussi — pas de redirection nécessaire');
            notifyAuthChange();
            handlingRef.current = false;
            return;
          }
        }

        // 2️⃣ Refresh impossible ou absent — redirection en dernier recours
        console.log('🔑 Refresh impossible, redirection vers Auth0 (dernier recours)...');
        lastRedirectTime = Date.now();
        
        localStorage.removeItem('auth0_token');
        localStorage.removeItem('auth0_refresh_token');
        
        const currentPath = window.location.pathname + window.location.search;
        if (!currentPath.includes('/auth/')) {
          sessionStorage.setItem('auth0_redirect_after_login', currentPath);
        }
        
        startAuth0PKCE('login', AUTH0_DOMAIN, AUTH0_CLIENT_ID, AUTH0_CALLBACK_URL);
      } catch (err) {
        console.error('❌ Erreur dans useAuthErrorHandler:', err);
      } finally {
        handlingRef.current = false;
      }
    };

    window.addEventListener('auth:error', handleAuthError);
    return () => window.removeEventListener('auth:error', handleAuthError);
  }, [navigate]);
}
